import { dropOverlappingSpans, segmentText } from './annotated-composer.jsx';
import { severityToRiskTier } from '../../shared/risk-tiers.js';
import { TelemetryEvent } from '../../shared/telemetry-events.js';

/**
 * Return true only for integer, non-empty ranges that can be applied to
 * `text` without changing what any offset addresses.
 */
function hasValidDraftSpanOffsets(span, textLength) {
  return (
    span !== null &&
    typeof span === 'object' &&
    Number.isInteger(span.start) &&
    Number.isInteger(span.end) &&
    span.start >= 0 &&
    span.start < span.end &&
    span.end <= textLength
  );
}

/**
 * A draft assessment is trusted only when it is an atomic { text, spans }
 * pair for the exact text currently being submitted. Bounds alone are not
 * enough: a debounced response for an older draft can still fit the newer
 * draft and point at different characters.
 */
export function isDraftAssessmentForText(assessment, expectedText) {
  return (
    assessment !== null &&
    typeof assessment === 'object' &&
    typeof expectedText === 'string' &&
    assessment.text === expectedText &&
    Array.isArray(assessment.spans) &&
    assessment.spans.every((span) => hasValidDraftSpanOffsets(span, expectedText.length))
  );
}

/**
 * Apply §6.3 DraftSpan replacements in place. Descending offsets preserve the
 * positions of spans that have not been processed yet. Invalid input is left
 * untouched so a malformed provider response cannot rewrite the wrong text.
 */
export function applyReplacements(text, spans) {
  if (typeof text !== 'string' || !Array.isArray(spans)) return text;
  if (spans.some((span) => !hasValidDraftSpanOffsets(span, text.length))) return text;
  const sorted = [...dropOverlappingSpans(spans)].sort((a, b) => b.start - a.start);
  let out = text;
  for (const span of sorted) {
    out =
      out.slice(0, span.start) +
      (span.replacement ?? out.slice(span.start, span.end)) +
      out.slice(span.end);
  }
  return out;
}

/**
 * Convert an exact draft assessment to the segment shape consumed by the
 * HardStopSheet. Returning null is deliberate: the sheet must not fabricate
 * redactable segments when the assessment is absent, stale, or malformed.
 */
export function toFixtureSpans(assessment, expectedText) {
  if (!isDraftAssessmentForText(assessment, expectedText)) return null;
  const { text, spans } = assessment;
  if (spans.length === 0) return null;
  return segmentText(text, spans).map((segment) =>
    segment.span
      ? {
          t: segment.t,
          risk: severityToRiskTier(segment.span.confidence),
          replacement: segment.span.replacement,
          inference: {
            trait: segment.span.inferredTrait,
            confidence: segment.span.confidence,
          },
        }
      : { t: segment.t }
  );
}

function responseFromAssessment(assessment, context = {}) {
  const { spans } = assessment;
  return {
    messageInferences: spans.map((span) => ({
      trait: span.inferredTrait,
      confidence: span.confidence,
      value: span.justification || '',
    })),
    spans,
    meterUpdate: context.meterUpdate ?? [],
    mirrorAggregate: context.mirrorAggregate ?? null,
  };
}

function normalizePreviewResponse(response) {
  if (response === null || typeof response !== 'object') {
    throw new TypeError('Send Policy preview must resolve to an object');
  }
  const spans = Array.isArray(response.spans) ? response.spans : [];
  const messageInferences = Array.isArray(response.messageInferences)
    ? response.messageInferences
    : spans.map((span) => ({
        trait: span.inferredTrait,
        confidence: span.confidence,
        value: span.justification || '',
      }));
  return { ...response, spans, messageInferences };
}

/**
 * Build the D4 send policy around injected adapters. The policy owns the
 * submit/preview/confirm/cancel state machine; AppShell only supplies current
 * values and renders the pending preview.
 *
 * Interface:
 *   submit(text)  -> Promise<sent message | null>
 *   confirm(textOverride?) -> Promise<sent message | null>
 *   cancel(textOverride?, { silent? }?) -> void
 *   pending      -> current preview or null
 *
 * `null` means no message was committed: the preview is open, the action was
 * blocked, a duplicate action was ignored, or the injected send/preview failed.
 * A committed send returns exactly the value resolved by `send`. All reads that
 * can change while an async preview is in flight are supplied as functions so
 * callbacks never use a stale deadline or draft assessment.
 */
export function createSendPolicy({
  getMode = () => ({ f2Enabled: false, agency: 'soft' }),
  getDraftAssessment = () => null,
  getCommitContext = () => ({}),
  getLimitReached = () => null,
  send,
  previewDraft,
  onPendingChange,
  onCommit,
  onCancel,
  onBusyChange,
  onFailure,
  scheduleReply,
  recordUiEvent = () => {},
} = {}) {
  if (typeof send !== 'function') throw new TypeError('Send Policy requires a send function');

  let pending = null;
  let submitInFlight = false;
  let confirmInFlight = false;

  const setPending = (next) => {
    pending = next;
    onPendingChange?.(next);
  };

  const fail = (error) => {
    onFailure?.(error);
    return null;
  };

  const commit = async (text, response) => {
    let result;
    try {
      result = await send(text);
    } catch (error) {
      return fail(error);
    }
    onCommit?.({ text, response, result });
    scheduleReply?.();
    return result;
  };

  const submit = async (text) => {
    recordUiEvent(TelemetryEvent.SEND_ATTEMPT);
    if (typeof text !== 'string' || text.trim().length === 0) return null;
    if (getLimitReached?.() || pending || submitInFlight || confirmInFlight) return null;

    submitInFlight = true;
    onBusyChange?.(true);
    try {
      const { f2Enabled, agency } = getMode?.() ?? {};
      if (!(f2Enabled && agency === 'hard')) return await commit(text);

      const assessment = getDraftAssessment?.();
      let response;
      if (isDraftAssessmentForText(assessment, text)) {
        response = responseFromAssessment(assessment, getCommitContext?.());
      } else {
        if (typeof previewDraft !== 'function')
          throw new TypeError('Send Policy requires previewDraft for an unscored hard send');
        response = normalizePreviewResponse(
          await previewDraft(text, {
            trigger: 'hard-send',
            isCurrent: () => !getLimitReached?.(),
          })
        );
      }

      // A preview can be an actual network call. Re-read the deadline after it
      // resolves rather than trusting the submit closure's old render.
      if (getLimitReached?.()) return null;

      if (response.messageInferences.length > 0) {
        setPending({
          text,
          response,
          spans: toFixtureSpans({ text, spans: response.spans }, text),
        });
        recordUiEvent(TelemetryEvent.HARDSTOP_SHOWN, {
          inferenceCount: response.messageInferences.length,
        });
        return null;
      }

      return await commit(text, response);
    } catch (error) {
      return fail(error);
    } finally {
      submitInFlight = false;
      onBusyChange?.(false);
    }
  };

  const confirm = async (textOverride) => {
    if (!pending || confirmInFlight) return null;

    // This is intentionally a synchronous check against the current getter:
    // the sheet can remain open while the session reaches its cutoff.
    const limitReached = getLimitReached?.();
    if (limitReached) {
      setPending(null);
      recordUiEvent(TelemetryEvent.HARDSTOP_SEND_BLOCKED_BY_LIMIT, {
        limit_reached: limitReached,
      });
      return null;
    }

    const preview = pending;
    const committedText = typeof textOverride === 'string' ? textOverride : preview.text;
    confirmInFlight = true;
    recordUiEvent(TelemetryEvent.HARDSTOP_ACK, { was_redacted: Boolean(textOverride) });
    // Clear before awaiting send: a synchronous double tap must not commit the
    // same pending preview twice. A failure leaves the draft in the composer so
    // the participant can retry through submit, and is surfaced by onFailure.
    setPending(null);
    onBusyChange?.(true);
    try {
      return await commit(committedText, preview.response);
    } finally {
      confirmInFlight = false;
      onBusyChange?.(false);
    }
  };

  const cancel = (textOverride, { silent = false } = {}) => {
    if (!pending || confirmInFlight) return;
    setPending(null);
    if (!silent) {
      recordUiEvent(TelemetryEvent.HARDSTOP_CANCEL, { saved: typeof textOverride === 'string' });
      if (typeof textOverride === 'string') onCancel?.(textOverride);
    }
  };

  const hardStop = Object.freeze({
    redactInvoked: () =>
      recordUiEvent(TelemetryEvent.HARDSTOP_REDACT_INVOKED, {
        spanCount: pending?.spans?.filter((span) => span.risk).length ?? 0,
      }),
    ackToggled: (acknowledged) =>
      recordUiEvent(TelemetryEvent.HARDSTOP_ACK_TOGGLED, { acknowledged }),
    redactionSpanToggle: (spanIndex, redacted) =>
      recordUiEvent(TelemetryEvent.HARDSTOP_REDACTION_SPAN_TOGGLE, { spanIndex, redacted }),
    restoreAll: () => recordUiEvent(TelemetryEvent.HARDSTOP_RESTORE_ALL),
  });

  return Object.freeze({
    get pending() {
      return pending;
    },
    submit,
    confirm,
    cancel,
    hardStop,
  });
}
